import { action } from 'mobx';
import { SessionStore } from './session.store';
import { AggregateFunction, AgtAnalyticsStore, SelectColumn, JoinClause, JoinType, CaseExpression } from './agt.analytics.store';

export interface IAgtAkumaAktionenModel {
	vtnrId: number;
	kw: number;
	campaignNumber: number;
	countCampaigns: number;
	bearbeitungsQuote: number;
	bearbeitungsQuotePass?: number;
	maz?: number;
	email?: number;
	brief?: number;
	inbound?: number;
	outbound?: number;
	grenzwertBearbeiteteAktion?: number;
	campaignCountCont?: number;
	campaignCountAgt?: number;
	campaignCountAmistaskAgency?: number;
	campaignCountAmistaskEmployee?: number;
}

export interface IAgtAkumaCampaignDetailModel {
	agtid: number;
	anzahlaktionstreffer: number;
	zuweisungagentur: number;
	zuweisungmitarbeiter: number;
	aktionstreffferbearbeitet: number;
	aktionstreffferunbearbeitet: number;
	bearbeitungsquote: number;
	bearbeitungsquotepass: number;
}

export class AgtAkumaAktionenModel implements IAgtAkumaAktionenModel {
	vtnrId: number;
	kw: number;
	campaignNumber: number;
	maz: number;
	email: number;
	brief: number;
	inbound: number;
	outbound: number;
	grenzwertBearbeiteteAktion: number;
	campaignCountCont: number;
	campaignCountAgt: number;
	campaignCountAmistaskAgency: number;
	campaignCountAmistaskEmployee: number;
	campaignCountTotal: number;
	countCampaigns: number;
	bearbeitungsQuote: number;
	bearbeitungsQuotePass: number;
	campaignDownloadTotal: number;

	constructor(data: any) {
		this.vtnrId = data.vtnr_id || 0;
		this.kw = data.kw || 0;
		this.campaignNumber = data.campaign_number || '';
		this.maz = data.maz || 0;
		this.email = data.email || 0;
		this.brief = data.brief || 0;
		this.inbound = data.inbound || 0;
		this.outbound = data.outbound || 0;
		this.grenzwertBearbeiteteAktion = data.grenzwert_bearbeitete_aktion || 0;
		this.campaignCountAgt = data.cagt || 0;
		this.campaignCountCont = data.cc || 0;
		this.campaignCountAmistaskAgency = data.ca || 0;
		this.campaignCountAmistaskEmployee = data.ce || 0;
		this.campaignCountTotal = this.campaignCountAmistaskAgency + this.campaignCountAmistaskEmployee;
		this.countCampaigns = data.count_campaigns || 0;
		this.bearbeitungsQuote = data.q || 0;
		this.bearbeitungsQuotePass = data.qp || 0;
		this.campaignDownloadTotal = data.dw || 0;
	}
}

export const schemaAkumaAktionenOverview = [
	{
		label: 'Top Aktionen',
		name: 'top',
		whenCondition: 'c.top_campaign = 1',
	},
	{
		label: 'Top Aktionen Sach',
		name: 'topsach',
		// Only count if EXCLUSIVELY Sach (not multiple LOBs)
		whenCondition: 'c.top_campaign = 1 and lob_sach = 1 and lob_leben = 0 and lob_kranken = 0 and lob_agi = 0 and lob_wuestenrot = 0 and lob_sonstiges = 0',
	},
	{
		label: 'Top Aktionen Leben',
		name: 'topleben',
		// Only count if EXCLUSIVELY Leben (not multiple LOBs)
		whenCondition: 'c.top_campaign = 1 and lob_leben = 1 and lob_sach = 0 and lob_kranken = 0 and lob_agi = 0 and lob_wuestenrot = 0 and lob_sonstiges = 0',
	},
	{
		label: 'Top Aktionen Kranken',
		name: 'topkranken',
		// Only count if EXCLUSIVELY Kranken (not multiple LOBs)
		whenCondition: 'c.top_campaign = 1 and lob_kranken = 1 and lob_sach = 0 and lob_leben = 0 and lob_agi = 0 and lob_wuestenrot = 0 and lob_sonstiges = 0',
	},
	{
		label: 'Top Aktionen Übergreifend',
		name: 'topuebergreifend',
		// Übergreifend = multiple main LOBs OR special LOBs (AGI, Wüstenrot, Sonstiges)
		whenCondition: 'c.top_campaign = 1 and ((lob_sach::int + lob_leben::int + lob_kranken::int) > 1 or lob_agi = 1 or lob_wuestenrot = 1 or lob_sonstiges = 1)',
	},
	{
		label: 'Aktionen',
		name: 'aktionen',
		whenCondition: 'c.top_campaign in (1,0) ',
	},
	{
		label: 'Aktionen Sach',
		name: 'sach',
		// Only count if EXCLUSIVELY Sach (not multiple LOBs)
		whenCondition: 'lob_sach = 1 and lob_leben = 0 and lob_kranken = 0 and lob_agi = 0 and lob_wuestenrot = 0 and lob_sonstiges = 0',
	},
	{
		label: 'Aktionen Leben',
		name: 'leben',
		// Only count if EXCLUSIVELY Leben (not multiple LOBs)
		whenCondition: 'lob_leben = 1 and lob_sach = 0 and lob_kranken = 0 and lob_agi = 0 and lob_wuestenrot = 0 and lob_sonstiges = 0',
	},
	{
		label: 'Aktionen Kranken',
		name: 'kranken',
		// Only count if EXCLUSIVELY Kranken (not multiple LOBs)
		whenCondition: 'lob_kranken = 1 and lob_sach = 0 and lob_leben = 0 and lob_agi = 0 and lob_wuestenrot = 0 and lob_sonstiges = 0',
	},
	{
		label: 'Aktionen Übergreifend',
		name: 'uebergreifend',
		// Übergreifend = multiple main LOBs OR special LOBs (AGI, Wüstenrot, Sonstiges)
		whenCondition: '((lob_sach::int + lob_leben::int + lob_kranken::int) > 1 or lob_agi = 1 or lob_wuestenrot = 1 or lob_sonstiges = 1)',
	},
];

export class AgtAkumaAktionenOverviewModel implements IAgtAkumaAktionenModel {
	vtnrId: number;
	kw: number;
	campaignNumber: number;
	countCampaigns: number;
	bearbeitungsQuote: number;
	download: number;
	view: number;

	constructor(data: any) {
		this.vtnrId = data.vtnr_id || 0;
		this.kw = data.kw || 0;
		this.campaignNumber = data.campaign_number || '';
		this.countCampaigns = data.count_campaigns || 0;
		this.bearbeitungsQuote = data.bearbeitungs_quote || 0;
		this.download = data.campaign_download || 0;
		this.view = data.campaign_view || 0;
		schemaAkumaAktionenOverview.forEach((s) => {
			(this as any)[s.name + 'CustumerCount'] = data[s.name + 'custcount'] || 0;
			(this as any)[s.name + 'AgencyCount'] = data[s.name + 'agencycount'] || 0;
			(this as any)[s.name + 'Quote'] = ((this as any)[s.name + 'AgencyCount'] / ((this as any)[s.name + 'CustumerCount'] || 1)) * 100;
			(this as any)[s.name + 'Download'] = data[s.name + 'download'] || 0;
			(this as any)[s.name + 'View'] = data[s.name + 'view'] || 0;
			const amistaskagencycount = data[s.name + 'amistaskagencycount'] || 0;
			const amistaskemployeecount = data[s.name + 'amistaskemployeecount'] || 0;
			(this as any)[s.name + 'Tasks'] = amistaskagencycount + amistaskemployeecount;
		});
	}
}

export class AgtAkumaAktionenStore extends AgtAnalyticsStore {
	queryName: string = '?akumaAktionen';
	constructor(session: SessionStore) {
		super(session, {
			tableName: 'agentur_akumaaktionen_calc',
			defaultColumns: [
				{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE },
				{ column: 'kw', aggregateFunction: AggregateFunction.NONE },
				{ column: 'campaign_number', aggregateFunction: AggregateFunction.NONE },
				{ column: 'maz', aggregateFunction: AggregateFunction.NONE },
				{ column: 'A.email', aggregateFunction: AggregateFunction.NONE },
				{ column: 'brief', aggregateFunction: AggregateFunction.NONE },
				{ column: 'inbound', aggregateFunction: AggregateFunction.NONE },
				{ column: 'outbound', aggregateFunction: AggregateFunction.NONE },
				{ column: 'grenzwert_bearbeitete_aktion', aggregateFunction: AggregateFunction.NONE },
				{ column: 'campaign_count_cont', aggregateFunction: AggregateFunction.NONE },
				{ column: 'campaign_count_amistask_agency', aggregateFunction: AggregateFunction.NONE },
				{ column: 'campaign_count_amistask_employee', aggregateFunction: AggregateFunction.NONE },
				{ column: 'bearbeitungs_quote', aggregateFunction: AggregateFunction.AVG },
				{ column: 'bearbeitungs_quote_pass', aggregateFunction: AggregateFunction.SUM },
			],
		});
		this.queryName += '?akumaAktionen';
	}

	async getDistinctKWs(): Promise<number[]> {
		const selectColumns: SelectColumn[] = [{ column: 'kw', aggregateFunction: AggregateFunction.NONE, distinct: true }];
		const results = await this.query(selectColumns);
		return results.map((data) => data.kw);
	}

	async aggregateByAgt(kw: number, ssVmFilter?: string[]): Promise<AgtAkumaAktionenModel[]> {
		let whereClause = kw ? `kw = ${kw}` : undefined;
		if (ssVmFilter && ssVmFilter.length > 0) {
			const filterValues = ssVmFilter.map((v) => `AND ${v}`);
			whereClause = whereClause + ' ' + filterValues.join(' ');
		}
		const selectColumns: SelectColumn[] = [
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, groupBy: true },
			{ column: 'campaign_number', aggregateFunction: AggregateFunction.COUNT_DISTINCT, alias: 'count_campaigns', groupBy: false },
			{ column: 'maz', aggregateFunction: AggregateFunction.SUM, alias: 'maz' },
			{ column: 'brief', aggregateFunction: AggregateFunction.SUM, alias: 'brief' },
			{ column: 'A.email', aggregateFunction: AggregateFunction.SUM, alias: 'email' },
			{ column: 'inbound', aggregateFunction: AggregateFunction.SUM, alias: 'inbound' },
			{ column: 'outbound', aggregateFunction: AggregateFunction.SUM, alias: 'outbound' },
			{ column: 'campaign_count_cont', aggregateFunction: AggregateFunction.SUM, alias: 'cc' },
			{ column: 'campaign_count_amistask_agency', aggregateFunction: AggregateFunction.SUM, alias: 'ca' },
			{ column: 'campaign_count_amistask_employee', aggregateFunction: AggregateFunction.SUM, alias: 'ce' },
			{ column: 'bearbeitungs_quote', aggregateFunction: AggregateFunction.AVG, alias: 'q' },
			{ column: 'bearbeitungs_quote_pass', aggregateFunction: AggregateFunction.SUM, alias: 'qp' },
			{ column: 'campaign_download', aggregateFunction: AggregateFunction.SUM, alias: 'dw' },
		];

		const results = await this.query(selectColumns, whereClause);
		const models = results.map((data) => new AgtAkumaAktionenModel(data));
		return models;
	}

	async aggregateByCampaign(kw: number, ssVmFilter?: string[]): Promise<AgtAkumaAktionenModel[]> {
		let whereClause = kw ? `kw = ${kw}` : undefined;
		if (ssVmFilter && ssVmFilter.length > 0) {
			const filterValues = ssVmFilter.map((v) => `AND ${v}`);
			whereClause = whereClause + ' ' + filterValues.join(' ');
		}
		const selectColumns: SelectColumn[] = [
			{ column: 'campaign_number', alias: 'campaign_number', groupBy: true },
			// { column: 'maz', aggregateFunction: AggregateFunction.SUM, alias: 'maz' },
			// { column: 'brief', aggregateFunction: AggregateFunction.SUM, alias: 'brief' },
			// { column: 'email', aggregateFunction: AggregateFunction.SUM, alias: 'email' },
			// { column: 'inbound', aggregateFunction: AggregateFunction.SUM, alias: 'inbound' },
			// { column: 'outbound', aggregateFunction: AggregateFunction.SUM, alias: 'outbound' },
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.COUNT_DISTINCT, alias: 'cagt' },
			{ column: 'campaign_count_cont', aggregateFunction: AggregateFunction.SUM, alias: 'cc' },
			{ column: 'campaign_count_amistask_agency', aggregateFunction: AggregateFunction.SUM, alias: 'ca' },
			{ column: 'campaign_count_amistask_employee', aggregateFunction: AggregateFunction.SUM, alias: 'ce' },
			// { column: 'bearbeitungs_quote', aggregateFunction: AggregateFunction.SUM, alias: 'q' },
			// { column: 'bearbeitungs_quote_pass', aggregateFunction: AggregateFunction.SUM, alias: 'qp' },
			{ column: 'campaign_download', aggregateFunction: AggregateFunction.SUM, alias: 'dw' },
		];

		const results = await this.query(selectColumns, whereClause);
		const models = results.map((data) => new AgtAkumaAktionenModel(data));
		if (ssVmFilter && ssVmFilter.length > 0) {
			// models.forEach(model => {
			// 	model.applySSVmFilter(ssVmFilter);
			// });
		}

		return models;
	}

	/**
	 * Campaign count aggregation - counts distinct campaigns per category
	 * Returns number of campaigns (Anzahl Aktionen) - used by OverviewListe
	 */
	async aggregateCampaignCountsByAgt(kw?: number, ssVmFilter?: string[]): Promise<AgtAkumaAktionenOverviewModel[]> {
		const joins: JoinClause[] = [
			{
				type: JoinType.INNER,
				table: 'akumaaktionen_campaigns',
				alias: 'c',
				condition: 'c.campaign_number = A.campaign_number',
			},
		];
		let whereClause = kw ? `kw = ${kw}` : undefined;

		if (ssVmFilter && ssVmFilter.length > 0) {
			const filterValues = ssVmFilter.map((v) => `AND ${v}`);
			whereClause = whereClause + ' ' + filterValues.join(' ');
		}

		const selectColumns: SelectColumn[] = [
			{ column: 'A.vtnr_id', aggregateFunction: AggregateFunction.NONE, groupBy: true },
			{ column: 'A.campaign_count_amistask_agency', aggregateFunction: AggregateFunction.SUM, alias: 'campaign_count_amistask_agency' },
		];

		// For each schema category, count distinct campaigns that match the condition
		schemaAkumaAktionenOverview.forEach((s) => {

			const when = s.whenCondition + ' and campaign_start_date <= now() and campaign_end_date >=now() ';

			selectColumns.push({
				aggregateFunction: AggregateFunction.COUNT_DISTINCT,
				alias: s.name + 'CustCount',
				caseExpression: {
					whenCondition: when,
					thenValue: 'A.campaign_number',
					elseValue: 'NULL',
				},
			});
			selectColumns.push({
				aggregateFunction: AggregateFunction.COUNT_DISTINCT,
				alias: s.name + 'AgencyCount',
				caseExpression: {
					// whenCondition: `${when} AND (A.campaign_count_amistask_agency > 0 OR A.campaign_count_amistask_employee > 0)`,
					whenCondition: `${when} AND (A.bearbeitungs_quote_pass_withdownload > 0)`,
					thenValue: 'A.campaign_number',
					elseValue: 'NULL',
				},
			});
			selectColumns.push({
				aggregateFunction: AggregateFunction.SUM,
				alias: s.name + 'AmistaskAgencyCount',
				caseExpression: {
					// whenCondition: `${when} AND (A.campaign_count_amistask_agency > 0 OR A.campaign_count_amistask_employee > 0)`,
					whenCondition: `${when} AND (A.bearbeitungs_quote_pass_withdownload > 0)`,
					thenValue: 'A.campaign_count_amistask_agency',
					elseValue: 'NULL',
				},
			});
			selectColumns.push({
				aggregateFunction: AggregateFunction.SUM,
				alias: s.name + 'AmistaskEmployeeCount',
				caseExpression: {
					// whenCondition: `${when} AND (A.campaign_count_amistask_agency > 0 OR A.campaign_count_amistask_employee > 0)`,
					whenCondition: `${when} AND (A.bearbeitungs_quote_pass_withdownload > 0)`,
					thenValue: 'A.campaign_count_amistask_employee',
					elseValue: 'NULL',
				},
			});
			selectColumns.push({
				aggregateFunction: AggregateFunction.SUM,
				alias: s.name + 'Download',
				caseExpression: {
					whenCondition: `${when} AND (A.campaign_download > 0)`,
					thenValue: 'A.campaign_download',
					elseValue: 'NULL',
				},
			});
			selectColumns.push({
				aggregateFunction: AggregateFunction.SUM,
				alias: s.name + 'View',
				caseExpression: {
					whenCondition: `${when} `,
					thenValue: 'A.campaign_view',
					elseValue: 'NULL',
				},
			});
		});

		const results = await this.query(selectColumns, whereClause, joins);
		const models = results.map((data) => new AgtAkumaAktionenOverviewModel(data));
		return models;
	}

	@action
	async findByAgenturAndKW(agtId: number, kw: number): Promise<AgtAkumaAktionenModel[]> {
		const results = await this.queryWithWhere(`vtnr_id = ${agtId} AND kw = ${kw}`);
		return results.map((data) => new AgtAkumaAktionenModel(data));
	}

	@action
	async getActiveAgenturen(kw: number, minMaz: number = 1): Promise<AgtAkumaAktionenModel[]> {
		const results = await this.queryWithWhere(`kw = ${kw} AND maz >= ${minMaz}`);
		return results.map((data) => new AgtAkumaAktionenModel(data));
	}

	@action
	async getCampaignDetailsByAgency(campaignNumber: number, kw?: number): Promise<IAgtAkumaCampaignDetailModel[]> {
		const cacheKey = `campaignDetails_${campaignNumber}_${kw || 'all'}`;
		// Check cache first
		const cached = this.getCachedByKey(cacheKey);
		if (cached) {
			return cached as IAgtAkumaCampaignDetailModel[];
		}

		const joins: JoinClause[] = [
			{
				type: JoinType.INNER,
				table: 'akumaaktionen_campaigns',
				alias: 'c',
				condition: 'c.campaign_number = A.campaign_number',
			},
		];

		const selectColumns: SelectColumn[] = [
			{ column: 'A.vtnr_id', aggregateFunction: AggregateFunction.NONE, alias: 'agtId', groupBy: true },
			{ column: 'A.campaign_count_cont', aggregateFunction: AggregateFunction.SUM, alias: 'anzahlAktionstreffer', groupBy: false },
			{ column: 'A.campaign_count_amistask_agency', aggregateFunction: AggregateFunction.SUM, alias: 'zuweisungAgentur', groupBy: false },
			{ column: 'A.campaign_count_amistask_employee', aggregateFunction: AggregateFunction.SUM, alias: 'zuweisungMitarbeiter', groupBy: false },
			{
				aggregateFunction: AggregateFunction.SUM,
				alias: 'aktionstreffferBearbeitet',
				caseExpression: {
					whenCondition: '(A.campaign_count_amistask_agency > 0 OR A.campaign_count_amistask_employee > 0) AND A.campaign_count_cont IS NOT NULL',
					thenValue: 'A.campaign_count_cont',
					elseValue: '0',
				},
			},
			{
				aggregateFunction: AggregateFunction.SUM,
				alias: 'aktionstreffferUnbearbeitet',
				caseExpression: {
					whenCondition:
						'(A.campaign_count_amistask_agency IS NULL OR A.campaign_count_amistask_agency = 0) AND (A.campaign_count_amistask_employee IS NULL OR A.campaign_count_amistask_employee = 0) AND A.campaign_count_cont IS NOT NULL',
					thenValue: 'A.campaign_count_cont',
					elseValue: '0',
				},
			},
			{ column: 'A.bearbeitungs_quote', aggregateFunction: AggregateFunction.AVG, alias: 'bearbeitungsQuote', groupBy: false },
			{ column: 'A.bearbeitungs_quote_pass', aggregateFunction: AggregateFunction.AVG, alias: 'bearbeitungsQuotePass', groupBy: false },
		];

		let whereClause = `A.campaign_number = ${campaignNumber}`;
		if (kw) {
			whereClause += ` AND A.kw = ${kw}`;
		}

		const results = await this.query(selectColumns, whereClause, joins);

		// Cache the results
		return this.cacheByKey(cacheKey, results) as IAgtAkumaCampaignDetailModel[];
	}
}
